import { Alert, Card, CardContent, Grid, Theme } from '@mui/material';
import { useDialog } from 'muibox';
import { useContext, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SettingsContext } from '../../../../../../store/SettingsContext';
import { UserContext } from '../../../../../../store/UserContext';
import { d2f, handleHttpError, handleHttpErrorResponse } from '../../../../../../utils/common';
import SextforceMetricsTrialOverview from './SextforceMetricsTrialOverview';
import SextforceMetricsTrialSelector from './SextforceMetricsTrialSelector';
// import SextforceMetricsSuspiciousUsersList from '../../SextforceMetricsSuspiciousUsersList';
import useAxios from '../../../../../../hooks/useAxios';
import { OnlyFansMetrics } from '../../../../../../hooks/useSextforceMetricsCampaignsOverview';
import useSextforceMetricsCountersLastOfEveryMonth from '../../../../../../hooks/useSextforceMetricsCountersLastOfEveryMonth';
import useSextforceMetricsSubscribedAtVsTotalSalesDetails from '../../../../../../hooks/useSextforceMetricsSubscribedAtVsTotalSalesDetails';
import useSextforceMetricsUsersSalesDetails from '../../../../../../hooks/useSextforceMetricsUsersSalesDetails';
import { OnlyFansSubscriber } from '../../../../../../hooks/useSubscriber';
import useSubscribers from '../../../../../../hooks/useSubscribers';
import { TotalSales } from '../../campaigns/details/SextforceMetricsCampaign';
import SextforceMetricsBoughtFromSettings from '../../SextforceMetricsBoughtFromSettings';
import SextforceMetricsChargebacksByProductGraph from '../../SextforceMetricsChargebacksByProductGraph';
import SextforceMetricsCrossReferenceEarnings from '../../SextforceMetricsCrossReferenceEarnings';
import SextforceMetricsEarningsByIntervalGraph from '../../SextforceMetricsEarningsByIntervalGraph';
import SextforceMetricsSalesByProductGraph from '../../SextforceMetricsSalesByProductGraph';
import SextforceMetricsSubscribedAtVsSalesSummary from '../../SextforceMetricsSubscribedAtVsSalesSummary';
import SextforceMetricsUsersIdScatterWithRiskLevelGraph from '../../SextforceMetricsUsersIdScatterWithRiskLevelGraph';
import SextforceMetricsUsersWithRiskLevelList from '../../SextforceMetricsUsersWithRiskLevelList';
import SextforceMetricsTrialCountersGraph from './SextforceMetricsTrialCountersGraph';
import SextforceMetricsTrialLink from './SextforceMetricsTrialLink';
import SextforceMetricsTrialSubscribedAtGraph from './SextforceMetricsTrialSubscribedAtGraph';

type Props = {
    subscriber: OnlyFansSubscriber | null;
    timezone: string;
    setTimezone: (timezone: string) => void;
    theme: Theme;
};

const SextforceMetricsTrial = (props: Props) => {
    const { subscriber, timezone, setTimezone, theme } = props;

    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const dialog = useDialog();
    const axios = useAxios();
    const queryClient = useQueryClient();

    const [selectedTrial, setSelectedTrial] = useState<{ trialId: string; enabled: boolean; startDate: Date | null; endDate: Date | null }>(
        {
            trialId: params.metricId || '',
            enabled: false,
            startDate: null,
            endDate: null,
        },
    );
    const [trialUrl, setTrialUrl] = useState<string | null>(null);
    const [trialsSelectorType, setTrialsSelectorType] = useState<{ includeInactive: boolean; type: string }>({
        includeInactive: searchParams.has('includeInactive')
            ? searchParams.get('includeInactive') === 'true'
            : localStorage.getItem('sextforceMetricsCampaignsOverviewSelectorType') === 'true' || false,
        type: searchParams.has('type') ? searchParams.get('type')! : 'trialLinkTrial',
    });
    const [trialData, setTrialData] = useState<OnlyFansMetrics | null>();
    const [trialTotalSales, setTrialTotalSales] = useState<TotalSales>({
        total: 0,
        totalWithoutSubscriptions: 0,
        resubscription: 0,
        resubscriptionCount: 0,
        subscription: 0,
        subscriptionCount: 0,
        tip: 0,
        tipCount: 0,
        message: 0,
        messageCount: 0,
        post: 0,
        postCount: 0,
        stream: 0,
        streamCount: 0,
        totalChargebacks: 0,
        totalChargebacksCount: 0,
        totalChargebacksWithoutSubscriptions: 0,
        totalChargebacksWithoutSubscriptionsCount: 0,
        totalChargebacksMessage: 0,
        totalChargebacksMessageCount: 0,
        totalChargebacksResubscription: 0,
        totalChargebacksResubscriptionCount: 0,
        totalChargebacksTip: 0,
        totalChargebacksTipCount: 0,
        totalChargebacksSubscription: 0,
        totalChargebacksSubscriptionCount: 0,
        totalChargebacksPost: 0,
        totalChargebacksPostCount: 0,
        totalChargebacksStream: 0,
        totalChargebacksStreamCount: 0,
    });
    const [showEarningsWithSubscriptions, setShowEarningsWithSubscriptions] = useState<boolean>(
        localStorage.getItem('showEarningsWithSubscriptions') !== null
            ? localStorage.getItem('showEarningsWithSubscriptions') === 'true'
            : true,
    );
    const [showEarningsAsGross, setShowEarningsAsGross] = useState<boolean>(
        localStorage.getItem('showEarningsAsGross') !== null ? localStorage.getItem('showEarningsAsGross') === 'true' : true,
    );

    const [isSavingSettings, setIsSavingSettings] = useState<boolean>(false);

    // Cross-reference only mounts when trialData is trialLinkTrial; warm list on load like campaign page.
    useSubscribers();

    // Fetch Report
    const fetchPromoCampgains = async (includeInactive: boolean, type: string): Promise<any> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber) {
            const query: string = `${settingsContext.routes.metrics.base}${params.userId}/trials?${new URLSearchParams({
                includeInactive: includeInactive ? 'true' : 'false',
                type,
            })}`;

            const data = await axios
                .get(query)
                .then(response => {
                    return response.data;
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                });

            return data;
        }

        return [];
    };

    const {
        data: trials,
        isLoading: trialsLoading,
        isFetched: trialsFetched,
    } = useQuery(
        ['metricsTrials', trialsSelectorType, params.userId],
        () => fetchPromoCampgains(trialsSelectorType.includeInactive, trialsSelectorType.type),
        {
            refetchOnWindowFocus: false,
            // Stale time 5 minutes
            staleTime: 60 * 1000 * 5,
            enabled: subscriber ? true : false,
        },
    );

    useEffect(() => {
        if (trials && selectedTrial && selectedTrial.trialId !== '') {
            const trial = trials.find((campaign: any) => campaign._id === selectedTrial.trialId);

            setTrialData(trial || null);
        }
    }, [navigate, selectedTrial, trials]);

    // Fetch Promo Campaign Counters
    const fetchTrialCounters = async (): Promise<
        | {
              _id: string;
              payload: {
                  claimsCount: number;
                  clicksCounts?: number;
                  subscribeCounts: number;
              };
          }[]
        | undefined
    > => {
        if (
            userContext.jwtToken &&
            settingsContext.apiKey &&
            'userId' in params &&
            params.userId &&
            subscriber &&
            selectedTrial &&
            selectedTrial.trialId &&
            selectedTrial.trialId !== ''
        ) {
            const query: string = `${settingsContext.routes.metrics.base}${params.userId}/trials/${selectedTrial.trialId}/counters`;

            const data = await fetch(query, {
                method: 'get',
                headers: {
                    Authorization: userContext.jwtToken,
                    apiKey: settingsContext.apiKey,
                },
            })
                .then(async response => {
                    if (response.ok) {
                        return response.json();
                    } else {
                        handleHttpErrorResponse(response, dialog);
                    }
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                });

            return data;
        }

        return [];
    };

    const {
        data: trialCounters,
        isLoading: trialCountersLoading,
        isFetched: trialCountersFetched,
    } = useQuery([`metricsTrialCounters`, selectedTrial, params.userId, selectedTrial.trialId], () => fetchTrialCounters(), {
        refetchOnWindowFocus: false,
        // Stale time 5 minutes
        staleTime: 60 * 1000 * 5,
        enabled: Boolean(subscriber && selectedTrial.trialId !== '' && trialsFetched),
    });

    const {
        data: trialSales,
        isLoading: trialSalesLoading,
        isFetched: trialSalesFetched,
    } = useSextforceMetricsUsersSalesDetails(
        params.userId,
        selectedTrial.trialId,
        'trial',
        selectedTrial.enabled ? selectedTrial.startDate : null,
        selectedTrial.enabled ? selectedTrial.endDate : null,
        trialCountersFetched,
    );

    const {
        data: trialSubscribedAtVsSales,
        isLoading: trialSubscribedAtVsSalesLoading,
        isFetched: trialSubscribedAtFetched,
    } = useSextforceMetricsSubscribedAtVsTotalSalesDetails(subscriber?._id, 'trial', selectedTrial.trialId, timezone, trialSalesFetched);

    const { data: countersLastOfEveryMonth, isLoading: countersLastOfEveryMonthLoading } = useSextforceMetricsCountersLastOfEveryMonth(
        params.userId,
        trialData?._id,
        trialData?.type,
        trialSubscribedAtFetched,
    );

    const trialCountersLoadingEffective = Boolean(subscriber && selectedTrial.trialId !== '') && (!trialsFetched || trialCountersLoading);

    const trialSalesStaggerPrereqs =
        Boolean(userContext.jwtToken && settingsContext.apiKey && params.userId && selectedTrial.trialId) && trialsFetched;
    const trialSalesLoadingEffective = trialSalesLoading || (trialSalesStaggerPrereqs && !trialCountersFetched);

    const subscribedAtStaggerPrereqs = Boolean(
        userContext.jwtToken && settingsContext.apiKey && params.userId && subscriber?._id && selectedTrial.trialId,
    );
    const trialSubscribedAtVsSalesLoadingEffective =
        trialSubscribedAtVsSalesLoading || (subscribedAtStaggerPrereqs && trialCountersFetched && !trialSalesFetched);

    const lastOfMonthStaggerPrereqs = Boolean(
        userContext.jwtToken && settingsContext.apiKey && params.userId && trialData?._id && trialData?.type,
    );
    const countersLastOfEveryMonthLoadingEffective =
        countersLastOfEveryMonthLoading || (lastOfMonthStaggerPrereqs && !trialSubscribedAtFetched);

    // Calculate total sales for the selected trial
    useEffect(() => {
        if (!trialSalesFetched || trialSalesLoading) {
            return;
        }
        if (trialSales && trialSales.length > 0) {
            // Initialize accumulator object
            const totals = {
                total: 0,
                totalWithoutSubscriptions: 0,
                resubscription: 0,
                resubscriptionCount: 0,
                subscription: 0,
                subscriptionCount: 0,
                message: 0,
                messageCount: 0,
                post: 0,
                postCount: 0,
                stream: 0,
                streamCount: 0,
                tip: 0,
                tipCount: 0,
                totalChargebacks: 0,
                totalChargebacksCount: 0,
                totalChargebacksWithoutSubscriptions: 0,
                totalChargebacksWithoutSubscriptionsCount: 0,
                totalChargebacksMessage: 0,
                totalChargebacksMessageCount: 0,
                totalChargebacksResubscription: 0,
                totalChargebacksResubscriptionCount: 0,
                totalChargebacksTip: 0,
                totalChargebacksTipCount: 0,
                totalChargebacksSubscription: 0,
                totalChargebacksSubscriptionCount: 0,
                totalChargebacksPost: 0,
                totalChargebacksPostCount: 0,
                totalChargebacksStream: 0,
                totalChargebacksStreamCount: 0,
            };

            // Single loop through the array
            for (const row of trialSales) {
                // Process all fields directly
                if (row.total) totals.total += d2f(row.total);
                if (row.totalWithoutSubscriptions) totals.totalWithoutSubscriptions += d2f(row.totalWithoutSubscriptions);

                if (row.totalResubscription) {
                    const value = d2f(row.totalResubscription);
                    totals.resubscription += value;
                    if (value > 0) totals.resubscriptionCount += 1;
                }

                if (row.totalSubscription) {
                    const value = d2f(row.totalSubscription);
                    totals.subscription += value;
                    if (value > 0) totals.subscriptionCount += 1;
                }

                if (row.totalMessage) {
                    const value = d2f(row.totalMessage);
                    totals.message += value;
                    if (value > 0) totals.messageCount += 1;
                }

                if (row.totalPost) {
                    const value = d2f(row.totalPost);
                    totals.post += value;
                    if (value > 0) totals.postCount += 1;
                }

                if (row.totalStream) {
                    const value = d2f(row.totalStream);
                    totals.stream += value;
                    if (value > 0) totals.streamCount += 1;
                }

                if (row.totalTip) {
                    const value = d2f(row.totalTip);
                    totals.tip += value;
                    if (value > 0) totals.tipCount += 1;
                }

                if (row.totalChargebacks) {
                    const value = d2f(row.totalChargebacks);
                    totals.totalChargebacks += value;
                    if (value > 0) totals.totalChargebacksCount += 1;
                }

                if (row.totalChargebacksWithoutSubscriptions) {
                    const value = d2f(row.totalChargebacksWithoutSubscriptions);
                    totals.totalChargebacksWithoutSubscriptions += value;
                    if (value > 0) totals.totalChargebacksWithoutSubscriptionsCount += 1;
                }

                if (row.totalChargebacksMessage) {
                    const value = d2f(row.totalChargebacksMessage);
                    totals.totalChargebacksMessage += value;
                    if (value > 0) totals.totalChargebacksMessageCount += 1;
                }

                if (row.totalChargebacksResubscription) {
                    const value = d2f(row.totalChargebacksResubscription);
                    totals.totalChargebacksResubscription += value;
                    if (value > 0) totals.totalChargebacksResubscriptionCount += 1;
                }

                if (row.totalChargebacksTip) {
                    const value = d2f(row.totalChargebacksTip);
                    totals.totalChargebacksTip += value;
                    if (value > 0) totals.totalChargebacksTipCount += 1;
                }

                if (row.totalChargebacksSubscription) {
                    const value = d2f(row.totalChargebacksSubscription);
                    totals.totalChargebacksSubscription += value;
                    if (value > 0) totals.totalChargebacksSubscriptionCount += 1;
                }

                if (row.totalChargebacksPost) {
                    const value = d2f(row.totalChargebacksPost);
                    totals.totalChargebacksPost += value;
                    if (value > 0) totals.totalChargebacksPostCount += 1;
                }

                if (row.totalChargebacksStream) {
                    const value = d2f(row.totalChargebacksStream);
                    totals.totalChargebacksStream += value;
                    if (value > 0) totals.totalChargebacksStreamCount += 1;
                }
            }

            // Apply Math.trunc and multiply by 100
            const newTotalSales: TotalSales = {
                total: Math.trunc(totals.total * 100),
                totalWithoutSubscriptions: Math.trunc(totals.totalWithoutSubscriptions * 100),
                resubscription: Math.trunc(totals.resubscription * 100),
                resubscriptionCount: totals.resubscriptionCount,
                subscription: Math.trunc(totals.subscription * 100),
                subscriptionCount: totals.subscriptionCount,
                message: Math.trunc(totals.message * 100),
                messageCount: totals.messageCount,
                post: Math.trunc(totals.post * 100),
                postCount: totals.postCount,
                stream: Math.trunc(totals.stream * 100),
                streamCount: totals.streamCount,
                tip: Math.trunc(totals.tip * 100),
                tipCount: totals.tipCount,
                totalChargebacks: Math.trunc(totals.totalChargebacks * 100),
                totalChargebacksCount: totals.totalChargebacksCount,
                totalChargebacksWithoutSubscriptions: Math.trunc(totals.totalChargebacksWithoutSubscriptions * 100),
                totalChargebacksWithoutSubscriptionsCount: totals.totalChargebacksWithoutSubscriptionsCount,
                totalChargebacksMessage: Math.trunc(totals.totalChargebacksMessage * 100),
                totalChargebacksMessageCount: totals.totalChargebacksMessageCount,
                totalChargebacksResubscription: Math.trunc(totals.totalChargebacksResubscription * 100),
                totalChargebacksResubscriptionCount: totals.totalChargebacksResubscriptionCount,
                totalChargebacksTip: Math.trunc(totals.totalChargebacksTip * 100),
                totalChargebacksTipCount: totals.totalChargebacksTipCount,
                totalChargebacksSubscription: Math.trunc(totals.totalChargebacksSubscription * 100),
                totalChargebacksSubscriptionCount: totals.totalChargebacksSubscriptionCount,
                totalChargebacksPost: Math.trunc(totals.totalChargebacksPost * 100),
                totalChargebacksPostCount: totals.totalChargebacksPostCount,
                totalChargebacksStream: Math.trunc(totals.totalChargebacksStream * 100),
                totalChargebacksStreamCount: totals.totalChargebacksStreamCount,
            };

            setTrialTotalSales(newTotalSales);
        } else {
            setTrialTotalSales({
                total: 0,
                totalWithoutSubscriptions: 0,
                resubscription: 0,
                resubscriptionCount: 0,
                subscription: 0,
                subscriptionCount: 0,
                message: 0,
                messageCount: 0,
                post: 0,
                postCount: 0,
                stream: 0,
                streamCount: 0,
                tip: 0,
                tipCount: 0,
                totalChargebacks: 0,
                totalChargebacksCount: 0,
                totalChargebacksWithoutSubscriptions: 0,
                totalChargebacksWithoutSubscriptionsCount: 0,
                totalChargebacksMessage: 0,
                totalChargebacksMessageCount: 0,
                totalChargebacksResubscription: 0,
                totalChargebacksResubscriptionCount: 0,
                totalChargebacksTip: 0,
                totalChargebacksTipCount: 0,
                totalChargebacksSubscription: 0,
                totalChargebacksSubscriptionCount: 0,
                totalChargebacksPost: 0,
                totalChargebacksPostCount: 0,
                totalChargebacksStream: 0,
                totalChargebacksStreamCount: 0,
            });
        }
    }, [trialSales, trialSalesLoading, trialSalesFetched, selectedTrial.trialId]);

    const saveSettings = (platform: string, associatedUsername: string, goalSpent: number, goalSubs: number) => {
        const doUpdate = async () => {
            if (
                userContext.jwtToken &&
                settingsContext.apiKey &&
                'userId' in params &&
                params.userId &&
                subscriber &&
                selectedTrial &&
                selectedTrial.trialId &&
                selectedTrial.trialId !== ''
            ) {
                const query: string = `${settingsContext.routes.metrics.base}${params.userId}/trials/${selectedTrial.trialId}`;

                setIsSavingSettings(true);

                const body: any = {
                    ...(platform !== '' && { platform }),
                    ...(associatedUsername !== '' && { associatedUsername }),
                    ...(goalSpent !== 0 && { goalSpent }),
                    ...(goalSubs !== 0 && { goalSubs }),
                };

                // Update campaign settings
                const updatedMetric = await fetch(query, {
                    method: 'put',
                    headers: {
                        Authorization: userContext.jwtToken,
                        apiKey: settingsContext.apiKey,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(body),
                })
                    .then(async response => {
                        if (response.ok) {
                            return response.json();
                        } else {
                            handleHttpErrorResponse(response, dialog);
                        }
                    })
                    .catch(error => {
                        console.error(error);
                        setIsSavingSettings(false);
                        handleHttpError(error, dialog);
                    });

                const newTrials = [...trials];
                const updatedTrialIndex: number = newTrials.findIndex((trial: any) => trial._id === updatedMetric._id);

                if (updatedTrialIndex) {
                    if (updatedMetric.associatedUsername) {
                        newTrials[updatedTrialIndex].associatedUsername = updatedMetric.associatedUsername;
                    }

                    if (updatedMetric.settings) {
                        newTrials[updatedTrialIndex].settings = updatedMetric.settings;
                    }
                }

                queryClient.setQueryData(['metricsTrials', selectedTrial], newTrials);

                setTrialData(updatedMetric);

                setIsSavingSettings(false);
            }
        };

        doUpdate();
    };

    return (
        <>
            <Card sx={{ width: '100%', marginBottom: theme.spacing(1) }}>
                <CardContent>
                    <Grid container flexGrow={1} spacing={1} alignItems="center">
                        <Grid item xs={12} sm>
                            <SextforceMetricsTrialSelector
                                trialsLoading={trialsLoading}
                                trialsSelectorType={trialsSelectorType}
                                setTrialsSelectorType={setTrialsSelectorType}
                                trials={trials}
                                trialId={selectedTrial}
                                setTrialId={setSelectedTrial}
                                setTrialUrl={setTrialUrl}
                                timezone={timezone}
                                setTimezone={setTimezone}
                                theme={theme}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <SextforceMetricsTrialOverview
                subscriber={subscriber}
                trialData={trialData}
                trialCounters={trialCounters}
                trialCountersLoading={trialCountersLoadingEffective}
                trialTotalSales={trialTotalSales}
                trialSales={trialSales}
                trialSalesLoading={trialSalesLoadingEffective}
                showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                setShowEarningsWithSubscriptions={setShowEarningsWithSubscriptions}
                showEarningsAsGross={showEarningsAsGross}
                setShowEarningsAsGross={setShowEarningsAsGross}
                theme={theme}
            />

            <SextforceMetricsTrialLink trialUrl={trialUrl} theme={theme} />

            {trialData && trialData.type === 'trialLinkTrial' && (
                <SextforceMetricsCrossReferenceEarnings
                    metric={trialData}
                    metricType="trial"
                    showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                    showEarningsAsGross={showEarningsAsGross}
                    theme={theme}
                />
            )}

            <Grid container spacing={1} flexGrow={0} justifyContent="center" sx={{ marginBottom: 1 }}>
                <Grid item xs={12}>
                    <Alert variant="filled" severity="warning">
                        Please note that only trials that started after you started using Sextforce are guaranteed to give an accurate total
                        results. Because trials can be consumed by existing fans, there could be a situation that the user that consumed a
                        trial is long gone so their information caanot be retrieved in retrospect.
                        <br />
                        <br />
                        Similarly, because a user can already exist on the account when they consume a trial link, the total revenue made
                        from the trial, and the fan's individual amount spent is shown below, though completely accurate, it could also
                        overlap with another trial link if that same user consumed another link.
                        <br />
                        <br />
                        All earnings are calculated from when the trial/promo started and onwards. Any previous sales to the users who
                        consumed the trial/promo are ignored.
                    </Alert>
                </Grid>
            </Grid>

            <SextforceMetricsBoughtFromSettings
                subscriber={subscriber}
                metricData={trialData}
                saveSettings={saveSettings}
                isSavingSettings={isSavingSettings}
                theme={theme}
            />

            <SextforceMetricsTrialCountersGraph
                metricId={trialData?._id || ''}
                trialCounters={{ metricsDatas: trialCounters }}
                trialCountersLoading={trialCountersLoadingEffective}
                timezone={timezone}
                theme={theme}
            />

            <SextforceMetricsTrialSubscribedAtGraph
                metricId={trialData && trialData._id ? trialData._id : ''}
                trialSubscribedAtVsSales={trialSubscribedAtVsSales}
                trialSubscribedAtVsSalesLoading={trialSubscribedAtVsSalesLoadingEffective}
                showEarnings={true}
                showEarningsAsGross={showEarningsAsGross}
                showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                timezone={timezone}
                theme={theme}
            />

            <SextforceMetricsSubscribedAtVsSalesSummary
                metricId={trialData && trialData._id ? trialData._id : ''}
                promoCampaignSubscribedAtVsSales={trialSubscribedAtVsSales}
                promoCampaignSubscribedAtVsSalesLoading={trialSubscribedAtVsSalesLoadingEffective}
                countersLastOfEveryMonth={countersLastOfEveryMonth}
                countersLastOfEveryMonthLoading={countersLastOfEveryMonthLoadingEffective}
                showEarningsAsGross={showEarningsAsGross}
                showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                timezone={timezone}
            />

            <SextforceMetricsEarningsByIntervalGraph
                subscriber={subscriber}
                metricId={trialData && trialData._id ? trialData._id : ''}
                metricType={trialData && trialData.type ? trialData.type : undefined}
                showEarningsAsGross={showEarningsAsGross}
                showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                timezone={timezone}
                theme={theme}
            />

            <SextforceMetricsSalesByProductGraph
                totalSales={trialTotalSales}
                totalSalesLoading={trialSalesLoadingEffective}
                showEarningsAsGross={showEarningsAsGross}
                theme={theme}
            />

            <SextforceMetricsChargebacksByProductGraph
                totalSales={trialTotalSales}
                totalSalesLoading={trialSalesLoadingEffective}
                showEarningsAsGross={showEarningsAsGross}
                theme={theme}
            />

            <SextforceMetricsUsersIdScatterWithRiskLevelGraph users={trialSales || []} />

            <SextforceMetricsUsersWithRiskLevelList
                metricId={selectedTrial.trialId}
                promoCampaignTotalSales={trialSales}
                promoCampaignTotalSalesLoading={trialSalesLoadingEffective}
                showEarningsAsGross={showEarningsAsGross}
                showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                metricStartDate={trialData && trialData.payload && trialData.payload.createdAt}
                metricType={trialData && trialData.type ? trialData.type : undefined}
                timezone={timezone}
                theme={theme}
            />
        </>
    );
};

export default SextforceMetricsTrial;
